import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import {
	SOFTPHONE_DEFAULT_FALLBACK_KIND,
	type SoftphoneFallbackKindModel,
} from '../../constants/softphone-ring-strategies';
import { BaseEntity } from './base.entity';
import { GroupEntity } from './group.entity';
import { RbacRoleEntity } from './rbac-role.entity';
import { SoftphoneNumberEntity } from './softphone-number.entity';
import { UserEntity } from './user.entity';

/**
 * Per-number runtime config — which rep / group / role this number
 * rings on inbound calls, plus the per-rep tuning knobs (ring timeout,
 * DND, presence).
 *
 * Split out from `SoftphoneNumberEntity` so reassignment doesn't
 * disturb the carrier-side row (the underlying Twilio IncomingPhoneNumber
 * stays the same when a number moves between destinations; only the
 * config row changes).
 *
 * **Destination model (DD.4.D)**: a number routes to EXACTLY ONE of:
 *   - a User (`fkUserIdAssignedTo` set) — the original 1:1 assignment
 *   - a Group (`fkGroupId` set) — hunt-group across the group's
 *     softphone-enabled, non-DND members; strategy from
 *     `SoftphoneGroupConfigEntity` or tenant default
 *   - an RbacRole (`fkRbacRoleId` set) — hunt-group across users
 *     assigned to the RBAC role; strategy from
 *     `SoftphoneRoleConfigEntity` or tenant default
 *
 * Mutual exclusivity is enforced at the application layer (admin
 * save endpoint) — keeping it out of the schema makes the
 * "reassign from User to Group" admin operation a single UPDATE
 * rather than a multi-step transaction.
 *
 * The rep-specific tuning fields (`ringTimeoutSeconds`, `isDndEnabled`,
 * `isOnCall`, `lastCallCompletedAt`) only meaningfully apply when the
 * destination IS a User. For Group / Role destinations, those
 * concepts live on the per-rep config row of each member; this row's
 * values are ignored.
 */
@Index('softphone_number_config_number_pk', ['fkSoftphoneNumberId'], {
	unique: true,
})
@Index('softphone_number_config_user_idx', ['fkUserIdAssignedTo'])
@Index('softphone_number_config_group_idx', ['fkGroupId'])
@Index('softphone_number_config_role_idx', ['fkRbacRoleId'])
@Index('softphone_number_config_last_call_completed_idx', [
	'lastCallCompletedAt',
])
// The "at most one `isDefaultOutbound = true` per user" invariant is
// enforced purely in the application layer — `setMyDefaultOutboundNumber`
// is the sole writer and clears the user's other rows before setting one,
// inside a transaction whose user-keyed clear takes pessimistic row locks
// (via the auditable-manager guard) so concurrent setters serialize. There
// is intentionally no DB-level unique index: the only way to enforce it at
// the DB layer in MySQL is a generated-column unique index, and that
// mechanism (the lone generated column in the codebase) broke the custom
// entity-sync — see the removal commit. Don't reintroduce a generated column.
@Entity('softphone_number_config')
export class SoftphoneNumberConfigEntity extends BaseEntity {
	@Column('int', { name: 'fk_softphone_number__id', unsigned: true })
	public fkSoftphoneNumberId!: number;

	/**
	 * Set when this number's destination is a single User. Mutually
	 * exclusive with `fkGroupId` and `fkRbacRoleId` (admin layer
	 * enforces — schema permits null for the Group / Role cases).
	 *
	 * Made nullable in DD.4.D's migration; previously NOT NULL when
	 * User was the only supported destination type.
	 */
	@Column('int', {
		name: 'fk_user__id_assignedTo',
		unsigned: true,
		nullable: true,
	})
	public fkUserIdAssignedTo!: number | null;

	/**
	 * Set when this number's destination is a Group hunt-group.
	 * Inbound calls ring the group's softphone-enabled, non-DND
	 * members per the group's configured ring strategy
	 * (`SoftphoneGroupConfigEntity.ringStrategy` or tenant default).
	 */
	@Column('int', {
		name: 'fk_group__id',
		unsigned: true,
		nullable: true,
	})
	public fkGroupId!: number | null;

	/**
	 * Set when this number's destination is an RbacRole hunt-group.
	 * Inbound calls ring the role's assigned users per the role's
	 * configured ring strategy (`SoftphoneRoleConfigEntity.ringStrategy`
	 * or tenant default).
	 */
	@Column('int', {
		name: 'fk__rbac_role__id',
		unsigned: true,
		nullable: true,
	})
	public fkRbacRoleId!: number | null;

	/**
	 * No-answer fallback choice for User-destination numbers (DD.4.E,
	 * extended for the seven-kind model). Defaults to `'none'` — a
	 * brand-new line hangs up rather than recording silence into a
	 * voicemail with no greeting. Once the rep configures the
	 * underlying setting for any of the other six kinds, they can
	 * flip the active choice freely; per-kind config in the columns
	 * below is preserved across switches so a rep who flips
	 * voicemail → aiAgent → voicemail keeps their original greeting.
	 *
	 * Forwarding kinds (`forwardToNumber/User/Group/Role`) are
	 * permitted here because this entity sits on a direct-User ring;
	 * Group / Role configs use a narrower set (`none` / `voicemail` /
	 * `aiAgent` only) per the "only direct rings get forwarded" rule.
	 */
	@Column('varchar', {
		name: 'fallbackKind',
		length: 24,
		default: () => `'${SOFTPHONE_DEFAULT_FALLBACK_KIND}'`,
	})
	public fallbackKind!: SoftphoneFallbackKindModel;

	/**
	 * Set when `fallbackKind = 'aiAgent'` — which AI agent the
	 * fallback redirects to (`ConversationAgentEntity._id`). Stored
	 * even when the active fallback isn't `aiAgent` so a rep who
	 * temporarily flips to a different kind doesn't lose their
	 * agent selection.
	 */
	@Column('int', {
		name: 'fallbackAiAgentId',
		unsigned: true,
		nullable: true,
	})
	public fallbackAiAgentId!: number | null;

	/**
	 * URL of the rep's recorded voicemail greeting (Twilio Assets
	 * URL, populated by DD.4.G's audio-capture flow). Null = use
	 * the system's default TTS greeting. Only meaningful when
	 * `fallbackKind = 'voicemail'`, but persisted across kind
	 * switches so the greeting survives a flip-and-flip-back.
	 */
	@Column('varchar', {
		name: 'voicemailGreetingUrl',
		length: 512,
		nullable: true,
	})
	public voicemailGreetingUrl!: string | null;

	/**
	 * E.164 destination for `fallbackKind = 'forwardToNumber'`. Can
	 * be the rep's mobile, a different desk phone, an answering
	 * service, or any other PSTN number. Persisted across kind
	 * switches.
	 */
	@Column('varchar', {
		name: 'fallbackForwardNumber',
		length: 32,
		nullable: true,
	})
	public fallbackForwardNumber!: string | null;

	/**
	 * Target user for `fallbackKind = 'forwardToUser'`. The forward
	 * rings ONLY that user's softphone clients — it does not cascade
	 * through their own ring strategy or destination chain. Persisted
	 * across kind switches.
	 */
	@Column('int', {
		name: 'fallbackForwardUserId',
		unsigned: true,
		nullable: true,
	})
	public fallbackForwardUserId!: number | null;

	/**
	 * Target group for `fallbackKind = 'forwardToGroup'`. The forward
	 * re-enters ring orchestration on the target group with the
	 * originating user excluded from the candidate set (prevents the
	 * call bouncing back to the rep who configured the forward).
	 * Persisted across kind switches.
	 */
	@Column('int', {
		name: 'fallbackForwardGroupId',
		unsigned: true,
		nullable: true,
	})
	public fallbackForwardGroupId!: number | null;

	/**
	 * Target RBAC role for `fallbackKind = 'forwardToRole'`. Same
	 * exclusion semantics as `forwardToGroup`. Persisted across kind
	 * switches.
	 */
	@Column('int', {
		name: 'fallbackForwardRoleId',
		unsigned: true,
		nullable: true,
	})
	public fallbackForwardRoleId!: number | null;

	/**
	 * URL of the rep's chosen hold music / hold message (Twilio
	 * Assets URL, populated by DD.4.G's audio-capture flow). Null
	 * = use the tenant default; if no tenant default, Twilio's
	 * Borghestral track. Played when this rep clicks Hold mid-call.
	 */
	@Column('varchar', {
		name: 'holdMusicUrl',
		length: 512,
		nullable: true,
	})
	public holdMusicUrl!: string | null;

	/**
	 * Whether the rep is currently on a softphone call. Maintained
	 * by `SoftphoneRepPresenceService` on call lifecycle events:
	 * stamped `true` on call-start, `false` on call-end. Used by
	 * the `longest-idle` ring strategy to filter out reps who can't
	 * be rung right now (a rep mid-call is effectively unavailable
	 * regardless of the lastCallCompletedAt sort).
	 *
	 * Defaults to `false` for newly assigned reps — they're available
	 * by default, just haven't received a call yet.
	 */
	// type 'boolean' (not raw 'tinyint') — see recordingEnabled on
	// SoftphoneNumberEntity: raw tinyint reads back as the NUMBER 1/0
	// and silently fails strict `=== true` checks (PD-14797).
	@Column({
		type: 'boolean',
		name: 'isOnCall',
		default: () => "'0'",
	})
	public isOnCall!: boolean;

	/**
	 * Wall-clock timestamp of the rep's most recently ENDED softphone
	 * call. Powers the `longest-idle` ring strategy: candidate reps
	 * are sorted ASC NULLS FIRST so reps who have never received a
	 * call get top priority, then reps who finished longest ago.
	 *
	 * Maintained by `SoftphoneRepPresenceService` — stamped on every
	 * call-end event regardless of strategy (the field is always
	 * useful for "who's been idle?" queries even outside the
	 * orchestrator). Indexed for efficient sort.
	 */
	@Column('datetime', {
		name: 'lastCallCompletedAt',
		nullable: true,
	})
	public lastCallCompletedAt!: Date | null;

	/** Seconds the inbound `<Dial>` rings before falling through to the
	 *  unanswered-fallback branch. Default 30s aligns with Phase 4
	 *  voicemail / ai-agent fallback expectations. */
	@Column('int', {
		name: 'ringTimeoutSeconds',
		unsigned: true,
		default: 30,
	})
	public ringTimeoutSeconds!: number;

	/**
	 * Do Not Disturb. When true, inbound calls skip ringing this rep
	 * entirely and route straight to voicemail; group transfers omit
	 * this rep from the simultaneous-ring set; user-targeted transfers
	 * are rejected ("rep is unavailable"). Toggle from the rep's own
	 * softphone status bar.
	 *
	 * Per-rep flag (lives on the per-number-config row, which is
	 * keyed by user). Reps who don't have a softphone number
	 * assigned can't be DND because they aren't reachable anyway.
	 */
	// type 'boolean' — raw tinyint reads back as the NUMBER 1/0 and
	// silently fails the `=== true` DND gates in routing/call-control
	// (DND never actually blocked calls, PD-14797).
	@Column({
		type: 'boolean',
		name: 'isDndEnabled',
		default: () => "'0'",
	})
	public isDndEnabled!: boolean;

	/**
	 * PD-14270 — the last voicemail_template this rep dropped, so the
	 * "Drop Voicemail" picker can default to it for one-click reuse down a
	 * call list. Soft pointer (no FK constraint): if the template is deleted
	 * the picker just falls back to no default. Additive nullable column.
	 */
	@Column('int', {
		name: 'lastVoicemailTemplateId',
		unsigned: true,
		nullable: true,
	})
	public lastVoicemailTemplateId?: number | null;

	/**
	 * Manual presence state set by the rep from their profile-menu
	 * picker (DD.13). Four values modeled on the MS Teams subset:
	 *   - 'available' (default) — full call experience: ring + pop
	 *   - 'busy'                — toast-only inbound indicator; rep
	 *                             is rung but call-pop is suppressed
	 *                             so they aren't yanked out of focus
	 *   - 'dnd'                 — skipped from ring fan-out; missed
	 *                             call goes to next rep / voicemail
	 *   - 'offline'             — same routing behavior as 'dnd' but
	 *                             distinct intent (rep is fully out)
	 *
	 * `isDndEnabled` (above) is kept in sync as the legacy boolean
	 * so v1 readers and existing routing/transfer-target filters
	 * keep working: 'dnd' | 'offline' → true; 'available' | 'busy' →
	 * false. New code reads `manualPresenceStatus` directly.
	 */
	@Column('varchar', {
		name: 'manualPresenceStatus',
		nullable: true,
		length: 16,
	})
	public manualPresenceStatus!: 'available' | 'busy' | 'dnd' | 'offline' | null;

	/**
	 * Auto-busy-during-call save slot (DD.13.b). When a call starts
	 * for a rep whose `manualPresenceStatus = 'available'`, the
	 * presence service:
	 *   1. Stashes 'available' here
	 *   2. Flips `manualPresenceStatus` → 'busy' so further inbound
	 *      calls get the toast-only treatment
	 * On call-end, the service restores `manualPresenceStatus` from
	 * this slot and clears it. Net effect: reps in Available auto-go
	 * to Busy while on a call and pop back when the call ends.
	 *
	 * Only set when the rep was Available pre-call — if they were
	 * already Busy / DND / Offline, the slot stays null and the
	 * status is left alone (their manual setting wins).
	 *
	 * Cleared on any manual `setManualPresence` write so a manual
	 * change during a call isn't undone on hang-up.
	 */
	@Column('varchar', {
		name: 'preCallPresenceStatus',
		nullable: true,
		length: 16,
	})
	public preCallPresenceStatus!:
		| 'available'
		| 'busy'
		| 'dnd'
		| 'offline'
		| null;

	/**
	 * After-Call Work (ACW) duration in seconds — how long the rep
	 * stays "busy" after a call ends so they can complete disposition
	 * / notes without being interrupted by another ring (DD.13.c).
	 *
	 * Semantics:
	 *   - `null` → use the system default (30s as of this writing,
	 *              constant `DEFAULT_ACW_DURATION_SECONDS`). Most rows
	 *              should sit here; admin can opt individual reps in /
	 *              out as needs become clear.
	 *   - `0`    → ACW disabled; presence restores immediately on
	 *              call-end (legacy pre-DD.13.c behavior).
	 *   - `> 0`  → ACW active; on call-end the rep stays `busy` and
	 *              `acwExpiresAt` is stamped to `now + value`. Inbound
	 *              calls skip them via the existing isDndEnabled /
	 *              isOnCall / 'busy' filtering pattern.
	 *
	 * Per-rep granularity for v1; tenant-level default is a constant in
	 * code. A future settings UI can move this to per-tenant + per-rep
	 * override without schema change.
	 */
	@Column('int', {
		name: 'acwDurationSeconds',
		nullable: true,
		unsigned: true,
	})
	public acwDurationSeconds!: number | null;

	/**
	 * When the current ACW window ends. Stamped on `markCallEnded` if
	 * the effective ACW duration is > 0; cleared by `endAcwNow` (rep
	 * clicked "Resume calls") or the periodic janitor sweep
	 * (`reconcileExpiredAcw`).
	 *
	 * Null when: rep is not in ACW (idle or in a live call). The pair
	 * `(manualPresenceStatus = 'busy', acwExpiresAt > now)` is the
	 * authoritative "in ACW" signal; readers should treat
	 * `acwExpiresAt` past or null as "not in ACW."
	 */
	@Column('datetime', { name: 'acwExpiresAt', nullable: true })
	public acwExpiresAt!: Date | null;

	/**
	 * Marks this row as the user's default outbound caller-ID
	 * (DD.9.1). When the rep dials out without explicitly choosing a
	 * from-number, the backend uses the SoftphoneNumberEntity backing
	 * the `isDefaultOutbound = true` row for that user. App-level
	 * invariant: at most one true per user; setting one true clears
	 * the others. Only meaningful for User-destination rows
	 * (`fkUserIdAssignedTo` populated) — Group / Role rows can't be
	 * an outbound caller-ID.
	 */
	// type 'boolean' — raw tinyint reads back as the NUMBER 1/0 and
	// silently fails `=== true` checks (the my-numbers list never
	// marked the default outbound number, PD-14797).
	@Column({
		type: 'boolean',
		name: 'isDefaultOutbound',
		default: () => "'0'",
	})
	public isDefaultOutbound!: boolean;

	@ManyToOne(() => SoftphoneNumberEntity, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_softphone_number__id', referencedColumnName: 'id' }])
	public fkSoftphoneNumber!: SoftphoneNumberEntity;

	@ManyToOne(() => UserEntity, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_user__id_assignedTo', referencedColumnName: 'id' }])
	public fkUserAssignedTo!: UserEntity | null;

	@ManyToOne(() => GroupEntity, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_group__id', referencedColumnName: 'id' }])
	public fkGroup!: GroupEntity | null;

	@ManyToOne(() => RbacRoleEntity, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk__rbac_role__id', referencedColumnName: 'id' }])
	public fkRbacRole!: RbacRoleEntity | null;
}
